import {
  applyBonusStackChoice,
  applyMove,
  applyStreak,
  applySwapChoice,
  chargeLevels,
  staircaseValues,
  WILD_KINDS,
  type ApplyMoveResult,
  type Card,
  type DeckConfig,
  type EngineEvent,
  type GameState,
  type GuessType,
  type HouseRules,
  type PlayerState,
  type StackIndex,
  type StarfallState,
  type Suit,
  type ValueCard,
  type WildCard,
  type WildKind,
} from "@fiveout/core";

/**
 * The machinery every scripted tutorial round shares.
 *
 * A tutorial is a real round played through the real engine — `applyMove` and
 * its siblings resolve every play, and the Pixi table animates the events they
 * return. Nothing here re-implements a rule, which is the point: a tutorial that
 * fakes its outcomes teaches whatever the faking happens to do, and drifts
 * silently the first time a rule changes underneath it.
 *
 * What IS faked is the luck. Before each play the cards it needs are spliced
 * onto the top of the deck it draws from (see `primeDeck`), so "call Match here"
 * always lands the Match. Everything downstream of the draw — chains, taken
 * piles, wild follow-ups, stars, scoring — is whatever the engine says it is.
 *
 * The board and the draws are therefore load-bearing together: change a draw
 * and the pile it lands on changes for every later step. Each tutorial's test
 * plays the whole thing and asserts the outcome of each step, so a change that
 * breaks the lesson fails there rather than in front of a first-time player.
 * The tutorials themselves are data: `basics.ts`, `bonusMoves.ts`,
 * `wildCards.ts` and `houseRules.ts`, listed in `tutorials.ts`.
 */

export const TUTORIAL_PLAYER_ID = "tutorial-you";
export const TUTORIAL_PLAYER_NAME = "You";
/** The other seat, in the tutorials whose lesson needs a second player (turns, locks, the last lap). */
export const TUTORIAL_OPPONENT_ID = "tutorial-sam";
export const TUTORIAL_OPPONENT_NAME = "Sam";

/** Copy that may need to name a value showing on the table. Given the state it should read against. */
export type TutorialCopy = string | ((state: GameState) => string);

/** One input the engine accepts, in the shape the tutorial scripts it. */
export type TutorialMove =
  /**
   * `anyGuess`: every guess on the pile is offered and whichever is tapped is
   * played — for a card that does not care (a wild), so the player is not told
   * what is coming before it comes. `guess` is then what the tests play.
   */
  | { kind: "guess"; stackIndex: StackIndex; guess: GuessType; anyGuess?: boolean }
  | { kind: "free"; stackIndex: StackIndex }
  /** One off-by-one bonus placement (`applyBonusStackChoice`). */
  | { kind: "bonus"; stackIndex: StackIndex }
  /** One owed swap (`applySwapChoice`); the two piles may be tapped in either order. */
  | { kind: "swap"; stackIndices: [StackIndex, StackIndex] }
  /** Spending a burning deck on a player (`applyStreak`). Not a turn: the spender plays one after it. */
  | { kind: "streak"; targetId: string };

/**
 * A point mid-play where the table holds still and the coach explains what
 * just happened, until the player taps on. This is how a lesson SHOWS first and
 * explains after: a wild is held up face up before anything is said about it,
 * a guessed card hovers turned over before the verdict, an effect has finished
 * before it is named.
 */
export interface TutorialPause {
  /** The event to hold on, by type. */
  on: EngineEvent["type"];
  /** Which occurrence of it in the play's events: 1-based, -1 for the last. Default 1. */
  nth?: number;
  /**
   * `showcase`: a played wild, held up face up in the middle of the table.
   * `reveal`: a guessed card turned over, hovering beside the card it was
   * guessed against. `after` (the default): once the event has finished.
   */
  at?: "showcase" | "reveal" | "after";
  /** Read against the board the play leaves. */
  text: TutorialCopy;
  title?: string;
}

export interface TutorialPlay {
  /**
   * Whose play. The player's own by default, which waits for their tap on the
   * one control the move needs. Anyone else's is played for them as soon as
   * the table is still, with the step's `watching` copy up.
   */
  by?: string;
  move: TutorialMove;
  /** Cards spliced onto the top of that player's deck immediately before the play resolves. */
  draws?: Card[];
  /** Where the animation stops for the coach, in the order they happen. */
  pauses?: TutorialPause[];
}

export interface TutorialPointAt {
  /** The player's own deck's count badge. */
  deck?: boolean;
  /** The opponent's deck's count badge. */
  opponentDeck?: boolean;
  /** Piles' count badges, by index. */
  stacks?: StackIndex[];
}

export interface TutorialStep {
  id: string;
  title: string;
  /** Shown before the player acts. Paragraphs are split on a blank line; `**bold**` is a lead-in. */
  prompt: TutorialCopy;
  /**
   * The plays this step makes, in order. The player's are offered one at a
   * time, each as the only control on the table, so the script can't be walked
   * off. Empty or absent for a step that is just read and dismissed.
   */
  plays?: TutorialPlay[];
  /** Shown while somebody else's play is waiting or animating. Defaults to naming whose turn it is. */
  watching?: TutorialCopy;
  /**
   * Shown once the last play has finished animating. A step with plays and no
   * debrief moves straight on to the next one, which is how a play that leaves
   * a choice owed (an off-by-one Match, a Swap) hands over to the step that
   * makes it.
   */
  debrief?: TutorialCopy;
  /** The debrief's own heading, when the step's would give away what the play was about to show. */
  debriefTitle?: string;
  /** Label for the button that leaves the step. */
  cta?: string;
  /**
   * Count badges an arrow points at while the prompt is up. A badge sits in a
   * card's corner, where a new player reads it as the card's value, so the
   * step that explains it shows which number it means rather than describing
   * where to look.
   */
  pointAt?: TutorialPointAt;
}

export interface Tutorial {
  /** The route's last segment: `/tutorial/<id>`. */
  id: string;
  /** As listed under How to Play. */
  title: string;
  /** A fresh scripted round. Called on every entry, so restarting is just remounting. */
  initialState: () => GameState;
  steps: TutorialStep[];
}

/** The value showing on a pile, for prompts that name it — so the copy can never disagree with the table. */
export function showing(state: GameState, index: StackIndex): number {
  const top = state.stacks[index].cards[0];
  if (!top || top.kind !== "value") {
    throw new Error(`tutorial: stack ${index} is not showing a value card`);
  }
  return top.value;
}

/** "a 7", "an 8", "an 11": a value as a prompt says it. */
export function aValue(value: number): string {
  return `${value === 8 || value === 11 ? "an" : "a"} **${value}**`;
}

/** The card a Peek is showing the player: the top of their deck. */
export function nextCard(state: GameState, playerId: string = TUTORIAL_PLAYER_ID): number {
  const top = state.players.find((p) => p.id === playerId)?.deck[0];
  if (!top || top.kind !== "value") {
    throw new Error("tutorial: the next card is not a value card");
  }
  return top.value;
}

export function renderCopy(copy: TutorialCopy, state: GameState): string {
  return typeof copy === "function" ? copy(state) : copy;
}

export function playerOf(play: TutorialPlay): string {
  return play.by ?? TUTORIAL_PLAYER_ID;
}

/** A play the tutorial makes on its own, rather than waiting for the player's tap. */
export function isAutoPlay(play: TutorialPlay): boolean {
  return playerOf(play) !== TUTORIAL_PLAYER_ID;
}

/** Whether a tap the player made is the play the step is waiting for. */
export function tapMatches(expected: TutorialMove, tapped: TutorialMove): boolean {
  switch (expected.kind) {
    case "guess":
      return (
        tapped.kind === "guess" &&
        tapped.stackIndex === expected.stackIndex &&
        (!!expected.anyGuess || tapped.guess === expected.guess)
      );
    case "free":
    case "bonus":
      return tapped.kind === expected.kind && tapped.stackIndex === expected.stackIndex;
    case "swap": {
      if (tapped.kind !== "swap") return false;
      const [a, b] = expected.stackIndices;
      const [x, y] = tapped.stackIndices;
      return (a === x && b === y) || (a === y && b === x);
    }
    case "streak":
      return tapped.kind === "streak" && tapped.targetId === expected.targetId;
  }
}

/**
 * Resolves one scripted play through the engine, deck primed first. Throws as
 * the engine does on an illegal one. `tapped` is the player's own tap, which
 * only differs from the script in the guess an `anyGuess` play was made with.
 */
export function applyPlay(state: GameState, play: TutorialPlay, tapped?: TutorialMove): ApplyMoveResult {
  const by = playerOf(play);
  const primed = primeDeck(state, play.draws, by);
  const move = play.move.kind === "guess" && play.move.anyGuess && tapped?.kind === "guess" ? { ...play.move, guess: tapped.guess } : play.move;
  switch (move.kind) {
    case "guess":
      return applyMove(primed, by, { type: "guess", stackIndex: move.stackIndex, guess: move.guess });
    case "free":
      return applyMove(primed, by, { type: "free-place", stackIndex: move.stackIndex });
    case "bonus":
      return applyBonusStackChoice(primed, by, { stackIndex: move.stackIndex });
    case "swap":
      return applySwapChoice(primed, by, { stackIndices: move.stackIndices });
    case "streak":
      return applyStreak(primed, by, move.targetId);
  }
}

/** The index in a play's events that a pause holds on, or -1 when the play never produces it. */
export function pauseIndex(events: readonly EngineEvent[], pause: TutorialPause): number {
  const hits = events.flatMap((e, i) => (e.type === pause.on ? [i] : []));
  const nth = pause.nth ?? 1;
  return (nth < 0 ? hits[hits.length + nth] : hits[nth - 1]) ?? -1;
}

/**
 * Puts a play's cards on top of a player's deck, dropping as many cards off the
 * front as it adds.
 *
 * Replacing rather than prepending is what keeps the deck count on screen
 * honest: the player watches it fall by exactly the cards they play, and rise
 * again only when a pile comes back to them. Prepending would make the deck
 * grow by itself between turns, which is the one thing the count is teaching
 * never happens.
 *
 * A card already on top with the same id is replaced by itself, which is how a
 * step plays a card an earlier step left showing (a Peek's).
 */
export function primeDeck(state: GameState, cards: Card[] | undefined, playerId: string = TUTORIAL_PLAYER_ID): GameState {
  if (!cards || cards.length === 0) return state;
  const next = structuredClone(state);
  const player = next.players.find((p) => p.id === playerId);
  if (!player) throw new Error(`tutorial: no player ${playerId} to prime`);
  if (player.deck.length < cards.length) {
    throw new Error("tutorial: deck ran too short to prime the next play");
  }
  player.deck = [...cards, ...player.deck.slice(cards.length)];
  return next;
}

// ---------------------------------------------------------------------------
// Card construction. Ids are namespaced by tutorial and role so nothing
// collides once taken piles start folding cards back into a deck.
// ---------------------------------------------------------------------------

export function card(id: string, value: number, suit: Suit): ValueCard {
  return { id, kind: "value", value, suit, deckIndex: 0 };
}

export function wildCard(id: string, kind: WildKind): WildCard {
  return { id, kind: "wild", wildIndex: WILD_KINDS.indexOf(kind) as WildCard["wildIndex"], wildKind: kind, deckIndex: 0 };
}

/** A half deck carries diamonds and spades only, so the tutorials' cards do too. */
export function draws(prefix: string, spec: (number | WildKind)[]): Card[] {
  return spec.map((s, i) =>
    typeof s === "number" ? card(`${prefix}-${i}`, s, i % 2 === 0 ? "diamonds" : "spades") : wildCard(`${prefix}-${i}`, s),
  );
}

/** The table's piles, top card first, as built for a tutorial's opening board. */
export function buildStacks(prefix: string, board: number[][]): GameState["stacks"] {
  return board.map((values, idx) => ({
    cards: values.map((value, i) => card(`${prefix}-board-${idx}-${i}`, value, i % 2 === 0 ? "spades" : "diamonds")),
  })) as GameState["stacks"];
}

/**
 * Every house rule off. A tutorial switches on only the rules its lesson is
 * about: a rule left on would narrow the piles a scripted step is allowed to
 * point at — Jourdan's Streaks and Vinny's Rule are always on everywhere else.
 */
export const NO_HOUSE_RULES: HouseRules = {
  vinnysRule: false,
  jourdansStreaks: false,
  blueShell: false,
  starfall: false,
  starfallOff: [],
  mercyRule: false,
  droughtRule: false,
  burningDeck: false,
};

export function tutorialPlayer(id: string, nickname: string, deck: Card[], extra: Partial<PlayerState> = {}): PlayerState {
  return { id, nickname, isAI: id !== TUTORIAL_PLAYER_ID, connected: true, turnsTaken: 0, streak: 0, deck, ...extra };
}

export function tutorialRound(opts: {
  id: string;
  deckConfig: DeckConfig;
  houseRules?: Partial<HouseRules>;
  stacks: GameState["stacks"];
  players: PlayerState[];
  starfall?: Pick<StarfallState, "stars" | "deck" | "rng">;
}): GameState {
  return {
    id: opts.id,
    phase: "playing",
    deckConfig: opts.deckConfig,
    houseRules: { ...NO_HOUSE_RULES, ...opts.houseRules },
    players: opts.players,
    currentPlayerIndex: 0,
    stacks: opts.stacks,
    turnTimerSeconds: null,
    streakLocks: [],
    streakTargets: [],
    pendingDecision: null,
    // Constellations is off unless the state carries it, so a tutorial that
    // teaches it builds the meter here — with its charge levels and standing
    // staircase read off the opening board exactly as a deal would, or the
    // first card played would light stars for patterns already on the table.
    ...(opts.starfall
      ? {
          starfall: {
            ...opts.starfall,
            bounty: null,
            levels: chargeLevels(opts.stacks),
            stair: staircaseValues(opts.stacks),
            last: null,
          },
        }
      : {}),
    winnerPlayerId: null,
    scores: null,
    cardsLeft: null,
    roundNumber: 1,
    version: 1,
  };
}
